import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, Settings, Hash, TrendingUp, FileText, Layout, 
  Link as LinkIcon, BarChart3, Activity, ArrowRight, Zap, Wrench, Lock, Unlock, Users
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/lib/api";
import { TOOL_ACCESS } from "@/config/toolAccess";
import { useAuth } from "@/context/AuthContext";

// Categories that have at least one free tool
const FREE_CATEGORIES = ['technical-seo', 'on-page-optimization', 'misc-projects'];

const iconMap = {
  Search, Settings, Hash, TrendingUp, FileText, Layout, 
  Link: LinkIcon, BarChart3, Activity, Wrench, Users
};

const colorClasses = {
  green: { bg: "bg-green-500/10", text: "text-green-500", border: "border-green-500/30" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-500", border: "border-blue-500/30" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-500", border: "border-purple-500/30" },
  orange: { bg: "bg-orange-500/10", text: "text-orange-500", border: "border-orange-500/30" },
  pink: { bg: "bg-pink-500/10", text: "text-pink-500", border: "border-pink-500/30" },
  cyan: { bg: "bg-cyan-500/10", text: "text-cyan-500", border: "border-cyan-500/30" },
  yellow: { bg: "bg-yellow-500/10", text: "text-yellow-500", border: "border-yellow-500/30" },
  indigo: { bg: "bg-indigo-500/10", text: "text-indigo-500", border: "border-indigo-500/30" },
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-500", border: "border-emerald-500/30" },
  slate: { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/30" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/30" },
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 30 } }
};

const defaultCategories = [
  { id: "seo-audit", name: "SEO Audit", description: "Comprehensive website SEO analysis", icon: "Search", tools_count: 2, color: "green" },
  { id: "technical-seo", name: "Technical SEO", description: "Sitemap validation, robots.txt checker, and more", icon: "Settings", tools_count: 7, color: "blue" },
  { id: "keyword-research", name: "Keyword Research", description: "Keyword density, suggestions, and analysis", icon: "Hash", tools_count: 8, color: "purple" },
  { id: "competitive-analysis", name: "Competitive Analysis", description: "Analyze competitor strategies and rankings", icon: "TrendingUp", tools_count: 2, color: "orange" },
  { id: "content-creation", name: "Content Creation", description: "AI-powered content generation and optimization", icon: "FileText", tools_count: 5, color: "pink" },
  { id: "on-page-optimization", name: "On-Page Optimization", description: "Meta tags, headings, and content structure", icon: "Layout", tools_count: 7, color: "cyan" },
  { id: "link-building", name: "Link Building", description: "Backlink analysis and outreach tools", icon: "Link", tools_count: 0, color: "yellow" },
  { id: "data-analysis", name: "Data Analysis", description: "Analytics and performance metrics", icon: "BarChart3", tools_count: 0, color: "indigo" },
  { id: "keyword-tracking", name: "Keyword Tracking", description: "Monitor keyword rankings over time", icon: "Activity", tools_count: 2, color: "emerald" },
  { id: "misc-projects", name: "Misc. Projects", description: "Specialized tools for specific tasks", icon: "Wrench", tools_count: 3, color: "slate" },
];

const SEO_TEAM_CATEGORY = { id: "seo-team", name: "SEO Team", description: "AI-powered SEO team members", icon: "Users", tools_count: 4, color: "amber", restricted: true };
const RESTRICTED_EMAIL = "reporting@emerald.consulting";

export default function HomePage() {
  const [categories, setCategories] = useState(defaultCategories);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const visibleCategories = user?.email === RESTRICTED_EMAIL
    ? [...categories, SEO_TEAM_CATEGORY]
    : categories;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/tools/categories`);
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        // Keep using default categories
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-full">
      {/* Hero Section */}
      <section className="relative py-16 px-6 lg:px-12 hero-gradient">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <Badge variant="outline" className="mb-4 border-green-500/30 text-green-500">
              <Zap className="w-3 h-3 mr-1" />
              Professional SEO Suite
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              SEO Workflow
              <span className="text-green-500 text-glow-green"> Tools</span>
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mb-8">
              Comprehensive toolkit for SEO professionals. Analyze, optimize, and track 
              your search performance with powerful AI-driven tools.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span>10 Tool Categories</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span>AI-Powered Analysis</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span>DataForSEO Integration</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {loading ? (
              Array.from({ length: 9 }).map((_, i) => (
                <Card key={i} className="bg-zinc-900 border-zinc-800 animate-pulse">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-zinc-800" />
                    <div className="h-5 w-32 bg-zinc-800 rounded mt-4" />
                    <div className="h-4 w-48 bg-zinc-800 rounded mt-2" />
                  </CardHeader>
                </Card>
              ))
            ) : (
              visibleCategories.map((category) => {
                const Icon = iconMap[category.icon] || Search;
                const colors = colorClasses[category.color] || colorClasses.green;
                
                return (
                  <motion.div key={category.id} variants={itemVariants}>
                    <Link to={`/tools/${category.id}`} data-testid={`category-${category.id}`}>
                      <Card className="bg-zinc-900/50 border-zinc-800 hover:border-green-500/40 transition-all duration-300 group cursor-pointer h-full">
                        <CardHeader>
                          <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center mb-2`}>
                            <Icon className={`w-6 h-6 ${colors.text}`} />
                          </div>
                          <CardTitle className="text-white font-heading text-xl flex items-center justify-between">
                            {category.name}
                            <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-green-500 group-hover:translate-x-1 transition-all" />
                          </CardTitle>
                          <CardDescription className="text-zinc-400">
                            {category.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <div className={`w-1.5 h-1.5 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
                              <span>{category.tools_count} tools available</span>
                            </div>
                            {FREE_CATEGORIES.includes(category.id) ? (
                              <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 text-xs">
                                <Unlock className="w-3 h-3 mr-1" />
                                Free Tools
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-500/30 text-amber-500 text-xs">
                                <Lock className="w-3 h-3 mr-1" />
                                Premium
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 px-6 lg:px-12 border-t border-zinc-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "Keyword Density", path: "/tools/keyword-research/keyword-density", icon: Hash, color: "purple" },
              { name: "Meta Analyzer", path: "/tools/on-page-optimization/meta-analyzer", icon: Layout, color: "cyan" },
              { name: "Sitemap Validator", path: "/tools/technical-seo/sitemap-validator", icon: Settings, color: "blue" },
              { name: "Content Generator", path: "/tools/content-creation/content-generator", icon: FileText, color: "pink" },
            ].map((action) => {
              const Icon = action.icon;
              const colors = colorClasses[action.color];
              return (
                <Link key={action.path} to={action.path} data-testid={`quick-${action.name.toLowerCase().replace(' ', '-')}`}>
                  <div className={`p-4 rounded-lg border ${colors.border} ${colors.bg} hover:bg-opacity-20 transition-all group cursor-pointer`}>
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                      <span className="text-sm font-medium text-white">{action.name}</span>
                      <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-white ml-auto group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
